// ATC Trust Loop — multi-state Add to Cart button + Live Activity Feed
// State machine: idle → loading → success/error → (retry →) idle
// Feed is session-scoped (sessionStorage), rendered as a single fixed panel (top-right).
// Cart drawer update via window.theme.renderCartResponse (exposed by theme.js).
// Context-aware drawer: opens on product page or first-ever add; shows toast otherwise.

(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  // ── Timings ───────────────────────────────────────────────────────────────

  const SUCCESS_RESET    = 2500;
  const ERROR_TO_RETRY   = 2000;
  const RETRY_AUTO_RESET = 8000;

  // ── Feed config ───────────────────────────────────────────────────────────

  const FEED_KEY      = 'laki_atc_feed';
  const FEED_MAX      = 5;
  const TS_REFRESH_MS = 10000;
  const SHOW_FEED     = !!(window.LakiATC?.showFeed);

  // ── Toast config ──────────────────────────────────────────────────────────

  const TOAST_DURATION = 4000;
  const RAPID_ADD_MS   = 3000;

  let _lastAddTime    = 0;
  let _lastCartData   = null;
  let _lastSectionHtml = null;
  let _feedMinimized  = !document.body.classList.contains('template-product');

  // ── Feed storage ──────────────────────────────────────────────────────────

  function getFeed() {
    try { return JSON.parse(sessionStorage.getItem(FEED_KEY) || '[]'); }
    catch { return []; }
  }

  function saveFeed(entries) {
    try { sessionStorage.setItem(FEED_KEY, JSON.stringify(entries)); }
    catch {}
  }

  function pushFeedEntry(title, status) {
    if (!SHOW_FEED) return;
    const entries = getFeed();
    entries.unshift({ title, status, ts: Date.now() });
    if (entries.length > FEED_MAX) entries.length = FEED_MAX;
    saveFeed(entries);
    renderFeed();
  }

  // ── Money formatting ──────────────────────────────────────────────────────

  function formatMoney(cents) {
    const fmt = window.theme?.moneyFormat || '${{amount}}';
    const val = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt.replace(/\{\{amount[^}]*\}\}/, val);
  }

  // ── Context-aware drawer logic ────────────────────────────────────────────
  // Rules:
  //   1. Always open on product page (high intent, upsell opportunity)
  //   2. Always open if cart was empty (first item — introduce the cart)
  //   3. Rapid repeat add on a non-empty cart → toast only (don't interrupt browsing)

  function shouldOpenDrawer(btn) {
    const isProductPage = document.body.classList.contains('template-product');
    const isFirstItem   = (btn._prevCartCount || 0) === 0;
    const isRapid       = (btn._prevCartCount || 0) > 0
                          && (Date.now() - _lastAddTime) < RAPID_ADD_MS;
    return !isRapid && (isProductPage || isFirstItem);
  }

  // ── Toast notification ────────────────────────────────────────────────────

  function dismissToast(toast) {
    toast.classList.remove('is-visible');
  }

  function showToast(btn, data) {
    let toast = document.getElementById('laki-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id        = 'laki-toast';
      toast.className = 'laki-toast';
      toast.setAttribute('role', 'alert');
      document.body.appendChild(toast);

      toast.addEventListener('click', (e) => {
        if (e.target.closest('.laki-toast__close')) { dismissToast(toast); return; }
        dismissToast(toast);
        applyCartSection(_lastSectionHtml);
        document.querySelector('laki-cart')?.open();
      });
    }

    const rawImg  = data.featured_image?.url || data.image;
    const imgSrc  = rawImg
      ? rawImg.replace(/(\.\w{3,4})(\?.*)?$/, (_, ext, qs) => `_80x80_crop_center${ext}${qs || ''}`)
      : null;
    const title   = data.product_title || btn?.dataset?.productTitle || 'Item';
    const variant = data.variant_title && data.variant_title !== 'Default Title'
      ? data.variant_title : null;
    const price   = data.final_price != null ? formatMoney(data.final_price) : null;

    toast.innerHTML =
      '<div class="laki-toast__inner">' +
        (imgSrc
          ? '<div class="laki-toast__img"><img src="' + imgSrc + '" alt="" width="52" height="52" loading="eager"></div>'
          : '') +
        '<div class="laki-toast__body">' +
          '<span class="laki-toast__label">' +
            '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
              '<polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            'Added to cart' +
          '</span>' +
          '<span class="laki-toast__title">' + esc(title) + '</span>' +
          (variant ? '<span class="laki-toast__variant">' + esc(variant) + '</span>' : '') +
          (price   ? '<span class="laki-toast__price">'   + price   + '</span>' : '') +
        '</div>' +
        '<button class="laki-toast__close" aria-label="Dismiss" type="button">' +
          '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
            '<line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
      '</div>' +
      '<button class="laki-toast__cta" type="button">' +
        'View cart ' +
        '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<polyline points="9 18 15 12 9 6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
        '</svg>' +
      '</button>';

    if (toast._timer) clearTimeout(toast._timer);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toast._timer = setTimeout(() => dismissToast(toast), TOAST_DURATION);
  }

  // ── Feed panel minimize / expand ─────────────────────────────────────────

  function minimizePanel() {
    _feedMinimized = true;
    const panel = document.getElementById('pt-atc-global-feed');
    if (!panel) return;
    panel.classList.add('is-minimized');
    const countEl = panel.querySelector('.pt-atc-feed__tab-count');
    if (countEl) countEl.textContent = getFeed().length || '';
  }

  function expandPanel() {
    _feedMinimized = false;
    const panel = document.getElementById('pt-atc-global-feed');
    if (panel) panel.classList.remove('is-minimized');
  }

  // ── Feed rendering ────────────────────────────────────────────────────────

  function formatAge(ts) {
    const s = Math.round((Date.now() - ts) / 1000);
    if (s < 60)  return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60)  return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
  }

  const FEED_ICONS = {
    success: `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline points="5 12 10 17 20 7" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    error: `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    retry: `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline points="1 4 1 10 7 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  };

  const STATUS_LABELS = {
    success: 'Added to cart',
    error:   'Failed to add',
    retry:   'Retry successful',
  };

  function buildFeedHTML(entries) {
    return entries.map(({ title, status, ts }) => {
      const short = title.length > 24 ? title.slice(0, 23) + '…' : title;
      const label = STATUS_LABELS[status] || status;
      return `<div class="pt-atc-feed__item pt-atc-feed__item--${esc(status)}" role="listitem">
        <span class="pt-atc-feed__text" title="${esc(title)}">${esc(short)}</span>
        <span class="pt-atc-feed__status">
          <span class="pt-atc-feed__icon" aria-hidden="true">${FEED_ICONS[status] || ''}</span>
          <span class="pt-atc-feed__status-label">${label}</span>
        </span>
        <span class="pt-atc-feed__time" data-ts="${ts}">${formatAge(ts)}</span>
      </div>`;
    }).join('');
  }

  // ── Singleton feed panel ──────────────────────────────────────────────────

  function ensurePanel() {
    let panel = document.getElementById('pt-atc-global-feed');
    if (panel) return panel;
    panel = document.createElement('div');
    panel.id = 'pt-atc-global-feed';
    panel.className = 'pt-atc-feed';
    panel.setAttribute('aria-label', 'Live Activity Feed');
    panel.setAttribute('aria-live', 'polite');
    panel.hidden = true;
    panel.innerHTML =
      '<div class="pt-atc-feed__tab" aria-hidden="true">' +
        '<span class="pt-atc-feed__dot"></span>' +
        '<span class="pt-atc-feed__tab-count"></span>' +
        '<span class="pt-atc-feed__tab-label">Live</span>' +
      '</div>' +
      '<div class="pt-atc-feed__header">' +
        '<span class="pt-atc-feed__dot" aria-hidden="true"></span>' +
        '<span class="pt-atc-feed__title">Live Activity Feed</span>' +
        '<button class="pt-atc-feed__minimize" aria-label="Minimize feed" type="button">' +
          '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<polyline points="9 18 15 12 9 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
      '</div>' +
      '<div class="pt-atc-feed__list" data-atc-feed-list role="list"></div>' +
      '<button class="pt-atc-feed__clear" type="button" hidden>Clear Log</button>';

    panel.querySelector('.pt-atc-feed__minimize').addEventListener('click', (e) => {
      e.stopPropagation();
      minimizePanel();
    });

    panel.querySelector('.pt-atc-feed__clear').addEventListener('click', (e) => {
      e.stopPropagation();
      saveFeed([]);
      renderFeed();
    });

    panel.addEventListener('click', () => {
      if (_feedMinimized) expandPanel();
    });

    document.body.appendChild(panel);
    return panel;
  }

  // Anchors the panel to the bottom edge of the whole header (logo bar +
  // nav row together), not just the buy box column — so it reads as
  // "hanging" from the header rather than floating mid-page.
  function positionPanel(panel) {
    if (window.innerWidth < 990) return;
    const headerGroup = document.getElementById('shopify-section-group-header-group');
    if (!headerGroup) return;
    const top = headerGroup.getBoundingClientRect().bottom;
    if (top > 0) panel.style.top = top + 'px';
  }

  function renderFeed() {
    if (!SHOW_FEED) return;
    const panel   = ensurePanel();
    const entries = getFeed();
    if (entries.length === 0) { panel.hidden = true; return; }
    panel.hidden = false;
    positionPanel(panel);

    const list = panel.querySelector('[data-atc-feed-list]');
    if (list) list.innerHTML = buildFeedHTML(entries);

    const clearBtn = panel.querySelector('.pt-atc-feed__clear');
    if (clearBtn) clearBtn.hidden = entries.length === 0;

    // Keep count badge in sync (shown in minimized tab)
    const countEl = panel.querySelector('.pt-atc-feed__tab-count');
    if (countEl) countEl.textContent = entries.length;

    // Apply minimized state
    panel.classList.toggle('is-minimized', _feedMinimized);
  }

  setInterval(() => {
    const panel = document.getElementById('pt-atc-global-feed');
    if (!panel || panel.hidden) return;
    panel.querySelectorAll('[data-ts]').forEach((el) => {
      el.textContent = formatAge(Number(el.dataset.ts));
    });
  }, TS_REFRESH_MS);

  // ── Button state machine ──────────────────────────────────────────────────

  function setAriaLive(btn, text) {
    const el = btn.closest('[data-atc-style]')?.querySelector('.pt-atc__sr');
    if (el) el.textContent = text;
  }

  function setState(btn, state) {
    if (btn._atcTimer) { clearTimeout(btn._atcTimer); btn._atcTimer = null; }
    btn.dataset.state = state;
    if (state === 'idle' || state === 'sold-out') setAriaLive(btn, '');
  }

  // Replace #shopify-section-cart-drawer outerHTML, or fall back to innerHTML swap
  function applyCartSection(sectionHtml) {
    if (!sectionHtml) return;
    const wrapper = document.getElementById('shopify-section-cart-drawer');
    if (wrapper) {
      wrapper.outerHTML = sectionHtml;
    } else {
      // Fallback: swap just the inner content of laki-cart
      const newDoc  = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const newCart = newDoc.getElementById('laki-cart');
      const curCart = document.getElementById('laki-cart');
      if (newCart && curCart) curCart.innerHTML = newCart.innerHTML;
    }
  }

  function applyCartUpdate(cartData, sectionHtml, openDrawer, lineItem) {
    _lastCartData    = cartData;
    _lastSectionHtml = sectionHtml;
    _lastAddTime     = Date.now();

    // Update header cart count badge
    if (typeof cartData?.item_count === 'number') {
      document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach((el) => {
        el.textContent = cartData.item_count;
      });
    }

    // Replace cart section HTML
    applyCartSection(sectionHtml);

    // Open drawer or show toast
    if (openDrawer) {
      document.querySelector('laki-cart')?.open();
    } else {
      showToast(null, lineItem || cartData);
    }

    window.LakiEvents?.emit?.(window.LakiEvents.EVENTS.CART_ADD, {
      count: cartData?.item_count,
      items: cartData?.items,
    });

    // Re-check core-charge quantities against whatever just changed (see
    // assets/core-charge-sync.js).
    window.LakiCoreCharge?.sync();
  }

  function handleSuccess(btn, lineItem) {
    const title  = btn.dataset.productTitle || 'Item';
    const status = btn._wasRetry ? 'retry' : 'success';

    setState(btn, 'success');
    btn.disabled = false;
    setAriaLive(btn, window.theme?.strings?.addedToCart || 'Item added to cart.');
    pushFeedEntry(title, status);

    const openDrawer = shouldOpenDrawer(btn);
    const root = window.Shopify?.routes?.root || '/';

    // /cart.js doesn't reliably return sections — fetch them from the
    // dedicated sections endpoint in parallel with cart metadata.
    Promise.all([
      fetch(`${root}cart.js`).then((r) => r.json()),
      fetch(`${root}?sections=cart-drawer`).then((r) => r.json()),
    ])
      .then(([cartData, sectionsData]) => {
        applyCartUpdate(cartData, sectionsData?.['cart-drawer'], openDrawer, lineItem);
      })
      .catch(() => {
        // Best-effort fallback: just open the drawer
        if (openDrawer) document.querySelector('laki-cart')?.open();
      });

    btn._atcTimer = setTimeout(() => {
      if (btn.dataset.state === 'success') setState(btn, 'idle');
    }, SUCCESS_RESET);
  }

  function handleError(btn) {
    const title = btn.dataset.productTitle || 'Item';

    setState(btn, 'error');
    btn.disabled = false;
    setAriaLive(btn, 'Failed to add item.');
    pushFeedEntry(title, 'error');

    btn._atcTimer = setTimeout(() => {
      if (btn.dataset.state !== 'error') return;
      btn.dataset.state = 'retry';
      setAriaLive(btn, 'Failed to add. Tap Try Again.');

      btn._atcTimer = setTimeout(() => {
        if (btn.dataset.state === 'retry') setState(btn, 'idle');
      }, RETRY_AUTO_RESET);
    }, ERROR_TO_RETRY);
  }

  function doFetch(btn) {
    const form = btn.dataset.formId
      ? document.getElementById(btn.dataset.formId)
      : null;

    if (!form) { handleError(btn); return; }

    const formData = new FormData(form);
    const root = window.Shopify?.routes?.root || '/';

    // Core Charge: only engages when the Liquid side found an exact price
    // match against the merchant's companion product (see
    // auto-parts-product.liquid / product-fitment.js) — every other
    // product's request is completely unaffected and still uses the plain
    // FormData path below.
    const coreChargeVariantId = document.querySelector('[data-core-charge-variant-id]')?.value || '';

    let fetchOptions;
    if (coreChargeVariantId) {
      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: formData.get('id'), quantity },
            // _core_charge: Shopify hides underscore-prefixed properties
            // from the customer-facing cart/order display, but theme code
            // can still read it to suppress this line's remove/quantity
            // controls (see cart-drawer.liquid / main-cart.liquid) — a
            // customer can otherwise freely delete a normal cart line.
            { id: coreChargeVariantId, quantity, properties: { _core_charge: 'true' } }
          ]
        })
      };
    } else {
      fetchOptions = {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      };
    }

    fetch(`${root}cart/add.js`, fetchOptions)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Unable to add item.');
        // A multi-item add returns { items: [...] } instead of a single line
        // item object — use the main product's entry (always first) so the
        // success toast/feed still shows the right title/price.
        return data.items ? data.items[0] : data;
      })
      .then((lineItem) => handleSuccess(btn, lineItem))
      .catch(() => handleError(btn));
  }

  // ── Event delegation ──────────────────────────────────────────────────────

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-atc-main]');
    if (!btn) return;

    const state = btn.dataset.state;
    if (state === 'loading' || state === 'sold-out') return;

    btn._wasRetry = (state === 'retry');

    // Capture cart count before the fetch so shouldOpenDrawer knows the pre-add state
    const countEl = document.querySelector('[data-cart-count], .cart-count-bubble');
    btn._prevCartCount = parseInt(countEl?.textContent?.trim() || '0', 10) || 0;

    setState(btn, 'loading');
    btn.disabled = true;
    setAriaLive(btn, 'Processing…');
    doFetch(btn);
  });

  // ── Init: restore feed from session ──────────────────────────────────────

  renderFeed();

  window.addEventListener('resize', () => {
    const panel = document.getElementById('pt-atc-global-feed');
    if (panel && !panel.hidden) positionPanel(panel);
  });

  // ── Hide feed when cart drawer or quick view opens; restore when it closes ──
  // renderFeed() re-applies its own "nothing to show" hidden state on
  // restore, so this never incorrectly reveals an empty panel.

  const FEED_HIDING_DRAWER_IDS = ['laki-cart', 'qv-drawer'];

  if (window.LakiEvents) {
    window.LakiEvents.on(window.LakiEvents.EVENTS.DRAWER_OPEN, ({ drawer }) => {
      if (!drawer || !FEED_HIDING_DRAWER_IDS.includes(drawer.id)) return;
      const panel = document.getElementById('pt-atc-global-feed');
      if (panel) panel.hidden = true;
    });

    window.LakiEvents.on(window.LakiEvents.EVENTS.DRAWER_CLOSE, ({ drawer }) => {
      if (!drawer || !FEED_HIDING_DRAWER_IDS.includes(drawer.id)) return;
      renderFeed();
    });
  }
})();
